import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { iconInfoCircle } from '@sorare/core/src/atoms/icons/system/iconInfoCircle.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardPullRulesButton } from '#src/components/essence/CardPullExperience/CardPullRulesButton/index.tsx';

import { EssenceFlavours } from './EssenceFlavours/index.tsx';
import type {
  CardPullInfos_cardPull,
  CardPullInfos_cardPullConfig,
  CardPullInfos_cardShardsChest,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  @media ${laptopAndAbove} {
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin: var(--double-unit);
    &.cardPullStarted,
    &.pulling {
      position: absolute;
      right: calc(var(--unit) * 6);
      top: calc(var(--unit) * 6);
      transform: translate(0, 0);
      transition: transform 0.2s ease-in-out;
    }
    &.cardPullStarted {
      transform: translate(calc(var(--unit) * 4), calc(var(--unit) * -4.5));
      margin: 0;
    }
    &.pulling {
      transform: translate(calc(var(--unit) * 4), calc(var(--unit) * -20));
    }
  }
`;

const Content = styled(Vertical)`
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: var(--double-unit) var(--double-unit) 0;
`;

const DropdownWrapper = styled.div`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  transition: transform 0.2s ease-in-out;
  z-index: 1;
  &.pulling {
    transform: translate(calc(var(--unit) * 2), calc(var(--unit) * -6));
  }
`;

type MobileDropdownProps = {
  children?: ReactNode;
  pulling: boolean;
};

const MobileDropdown = ({ children, pulling }: MobileDropdownProps) => {
  return (
    <DropdownWrapper className={classNames({ pulling })}>
      <Dropdown
        label={p => (
          <IconButton
            color="transparent"
            size="small"
            icon={iconInfoCircle}
            {...p}
          />
        )}
      >
        <BrandM className="text-center">
          <FormattedMessage defaultMessage="Infos" />
        </BrandM>
        <Content>{children}</Content>
      </Dropdown>
    </DropdownWrapper>
  );
};

type Props = {
  cardPull: Nullable<CardPullInfos_cardPull>;
  cardPullConfigs: CardPullInfos_cardPullConfig[];
  cardShardsChest: CardPullInfos_cardShardsChest;
  cardPullStarted: boolean;
  pulling: boolean;
};

export const CardPullInfos = ({
  cardPullStarted,
  cardPullConfigs,
  pulling,
  cardPull,
  cardShardsChest,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  return (
    <ConditionalWrapper
      wrap={!isLaptopAndAbove}
      Wrapper={MobileDropdown}
      props={{ pulling }}
    >
      <Wrapper className={classNames({ cardPullStarted, pulling })}>
        {!!cardShardsChest.id && (
          <EssenceFlavours
            cardPull={cardPull}
            id={cardShardsChest.id}
            rarity={cardShardsChest.rarity}
            fullWidth={!isLaptopAndAbove}
          />
        )}
        <CardPullRulesButton
          cardShardsChest={cardShardsChest}
          cardPullConfigs={cardPullConfigs}
          fullWidth={!isLaptopAndAbove}
        />
      </Wrapper>
    </ConditionalWrapper>
  );
};

CardPullInfos.fragments = {
  cardPullConfig: gql`
    fragment CardPullInfos_cardPullConfig on CardPullConfig {
      ...CardPullRulesButton_cardPullConfig
    }
    ${CardPullRulesButton.fragments.cardPullConfig}
  ` as TypedDocumentNode<CardPullInfos_cardPullConfig>,
  cardShardsChest: gql`
    fragment CardPullInfos_cardShardsChest on CardShardsChest {
      id
      rarity
      ...CardPullRulesButton_cardShardsChest
    }
    ${CardPullRulesButton.fragments.cardShardsChest}
  ` as TypedDocumentNode<CardPullInfos_cardShardsChest>,
  cardPull: gql`
    fragment CardPullInfos_cardPull on CardPull {
      id
      ...EssenceFlavours_cardPull
    }
    ${EssenceFlavours.fragments.cardPull}
  ` as TypedDocumentNode<CardPullInfos_cardPull>,
};
